import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Home, ChevronRight, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { HeroStyle } from '@/hooks/usePageDesign';
import heroFallback from '@/assets/hero-trades.jpg';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface ConfigurableHeroProps {
  heroStyle: HeroStyle;
  title: string;
  subtitle?: string;
  badge?: string;
  backgroundImage?: string;
  overlayOpacity?: number;
  /** Breadcrumb trail */
  breadcrumbs?: BreadcrumbItem[];
  showBreadcrumbs?: boolean;
  /** Trust badge items */
  trustBadges?: Array<{ icon: React.ReactNode; label: string }>;
  showTrustBadges?: boolean;
  /** CTA buttons */
  showCtaButtons?: boolean;
  ctaPrimaryText?: string;
  ctaPrimaryHref?: string;
  phone?: string;
  /** Rating display */
  showRating?: boolean;
  rating?: number;
  reviewCount?: number;
  /** Extra content below hero */
  children?: React.ReactNode;
}

/* ──────────────────── Full Bleed ──────────────────── */
const FullBleedHero: React.FC<ConfigurableHeroProps> = (props) => {
  const bgSrc = props.backgroundImage || heroFallback;
  const opacity = props.overlayOpacity ?? 0.6;

  return (
    <section className="relative overflow-hidden py-20 lg:py-28">
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50"
          style={{ opacity }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-background/60" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <HeroBreadcrumbs {...props} />
        <div className="max-w-3xl">
          <HeroContent {...props} />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
    </section>
  );
};

/* ──────────────────── Split Layout ──────────────────── */
const SplitHero: React.FC<ConfigurableHeroProps> = (props) => {
  const bgSrc = props.backgroundImage || heroFallback;

  return (
    <section className="relative overflow-hidden py-16 lg:py-24">
      <div className="container mx-auto px-4 relative z-10">
        <HeroBreadcrumbs {...props} />
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="max-w-xl">
            <HeroContent {...props} />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] hidden lg:block">
            <img
              src={bgSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────── Minimal Text ──────────────────── */
const MinimalTextHero: React.FC<ConfigurableHeroProps> = (props) => (
  <section className="relative overflow-hidden bg-foreground py-16 lg:py-24">
    <div
      className="absolute inset-0 z-0 opacity-[0.03]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}
    />

    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-3xl">
        {props.showBreadcrumbs && props.breadcrumbs && props.breadcrumbs.length > 0 && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-background/50 mb-8"
            aria-label="Breadcrumb"
          >
            {props.breadcrumbs.map((bc, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                {bc.href ? (
                  <Link to={bc.href} className="flex items-center gap-1 hover:text-background/80 transition-colors">
                    {bc.icon} {bc.label}
                  </Link>
                ) : (
                  <span className="text-background font-medium">{bc.label}</span>
                )}
              </React.Fragment>
            ))}
          </motion.nav>
        )}

        {props.badge && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-background/80 border border-background/20 rounded-full">
              <Sparkles className="h-3.5 w-3.5" />
              {props.badge}
            </span>
          </motion.div>
        )}

        {props.showRating && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="flex items-center gap-2.5 mb-5"
          >
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.floor(props.rating || 5) ? 'text-primary fill-primary' : 'text-background/30'}`} />
              ))}
            </div>
            <span className="font-semibold text-sm text-background">{props.rating || 4.9}</span>
            <span className="text-background/50 text-sm">({props.reviewCount || 100}+ reviews)</span>
          </motion.div>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-heading text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-background leading-[1.1] mb-5"
        >
          {props.title}
        </motion.h1>

        {props.subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-background/60 max-w-xl leading-relaxed"
          >
            {props.subtitle}
          </motion.p>
        )}

        {props.showCtaButtons && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
            <CtaButtons {...props} variant="minimal" />
          </motion.div>
        )}
      </div>
    </div>
  </section>
);

/* ──────────────────── Stacked ──────────────────── */
const StackedHero: React.FC<ConfigurableHeroProps> = (props) => (
  <section className="relative overflow-hidden bg-gradient-to-b from-muted/50 to-background py-16 lg:py-24">
    <div className="container mx-auto px-4 relative z-10">
      <HeroBreadcrumbs {...props} />
      <div className="max-w-2xl mx-auto text-center">
        {props.badge && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary border border-primary/20 rounded-full bg-primary/5">
              <Sparkles className="h-3.5 w-3.5" />
              {props.badge}
            </span>
          </motion.div>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-heading text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4"
        >
          <span className="gradient-text">{props.title}</span>
        </motion.h1>

        {props.subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed mb-6"
          >
            {props.subtitle}
          </motion.p>
        )}

        {props.showTrustBadges && props.trustBadges && props.trustBadges.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex flex-wrap justify-center gap-3 mb-8"
          >
            {props.trustBadges.map((badge, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/20"
              >
                {badge.icon}
                {badge.label}
              </span>
            ))}
          </motion.div>
        )}

        {props.showCtaButtons && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap justify-center gap-4">
            <CtaButtons {...props} />
          </motion.div>
        )}
      </div>
    </div>
  </section>
);

/* ──────────────────── Shared Subcomponents ──────────────────── */

const HeroBreadcrumbs: React.FC<ConfigurableHeroProps> = ({ showBreadcrumbs, breadcrumbs }) => {
  if (!showBreadcrumbs || !breadcrumbs || breadcrumbs.length === 0) return null;

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex items-center gap-2 text-sm text-muted-foreground mb-8"
      aria-label="Breadcrumb"
    >
      {breadcrumbs.map((bc, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          {bc.href ? (
            <Link to={bc.href} className="flex items-center gap-1 hover:text-foreground transition-colors">
              {bc.icon} {bc.label}
            </Link>
          ) : (
            <span className="text-foreground font-medium">{bc.label}</span>
          )}
        </React.Fragment>
      ))}
    </motion.nav>
  );
};

const HeroContent: React.FC<ConfigurableHeroProps> = (props) => (
  <>
    {props.badge && (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary border border-primary/20 rounded-full bg-primary/5">
          <Sparkles className="h-3.5 w-3.5" />
          {props.badge}
        </span>
      </motion.div>
    )}

    <motion.h1
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="font-heading text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground mb-4"
    >
      <span className="gradient-text">{props.title}</span>
    </motion.h1>

    {props.subtitle && (
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-lg text-foreground/90 max-w-2xl leading-relaxed mb-6"
      >
        {props.subtitle}
      </motion.p>
    )}

    {props.showTrustBadges && props.trustBadges && props.trustBadges.length > 0 && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-wrap gap-3 mb-8"
      >
        {props.trustBadges.map((badge, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/20 backdrop-blur-sm"
          >
            {badge.icon}
            {badge.label}
          </span>
        ))}
      </motion.div>
    )}

    {props.showCtaButtons && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="flex flex-wrap gap-4"
      >
        <CtaButtons {...props} />
      </motion.div>
    )}

    {props.children}
  </>
);

const CtaButtons: React.FC<ConfigurableHeroProps & { variant?: 'default' | 'minimal' }> = ({
  ctaPrimaryText = 'Contact Us Today!',
  ctaPrimaryHref = '/contact',
  phone,
  variant = 'default',
}) => {
  const isMinimal = variant === 'minimal';
  return (
    <>
      <Button asChild size="lg" className={`gap-2 ${isMinimal ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}>
        <Link to={ctaPrimaryHref}>
          {ctaPrimaryText}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
      {phone && (
        <Button
          asChild
          size="lg"
          variant="outline"
          className={`gap-2 ${isMinimal ? 'border-background/30 text-background hover:bg-background/10' : ''}`}
        >
          <a href={`tel:${phone.replace(/\D/g, '')}`}>
            <Phone className="h-4 w-4" />
            Call Now
          </a>
        </Button>
      )}
    </>
  );
};

/* ──────────────────── Main Export ──────────────────── */

export const ConfigurableHero: React.FC<ConfigurableHeroProps> = (props) => {
  switch (props.heroStyle) {
    case 'split':
      return <SplitHero {...props} />;
    case 'minimal_text':
      return <MinimalTextHero {...props} />;
    case 'stacked':
      return <StackedHero {...props} />;
    case 'full_bleed':
    default:
      return <FullBleedHero {...props} />;
  }
};
